import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../shared/utils/api';
import QuestionStats from './QuestionStats';
import QuestionSession from './QuestionSession';
import TopicPicker from './TopicPicker';
import PageToggle from '../../shared/components/PageToggle';
import ROUTES from '../../shared/constants/routes';

const TOGGLE_OPTS = [
  { value: 'mcq', label: 'MCQ Practice', route: ROUTES.QUESTIONS },
  { value: 'drills', label: 'Drills', route: ROUTES.DRILLS },
];

const MODULES = [
  { value: 'both', label: 'Both' },
  { value: 'FLK1', label: 'FLK1' },
  { value: 'FLK2', label: 'FLK2' },
];

const MODES = [
  { value: 'weakest', label: 'Weakest topics', desc: 'Focus on your lowest-scoring areas' },
  { value: 'topic', label: 'Specific topic', desc: 'Choose an L2 area or L3 subtopic' },
  { value: 'random', label: 'Random', desc: 'Questions from across all topics' },
  { value: 'incorrect', label: 'Review incorrect', desc: 'Retry questions you got wrong' },
];

export default function QuestionsPage() {
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [topicCounts, setTopicCounts] = useState([]);
  const [topicCountsLoading, setTopicCountsLoading] = useState(false);
  const [selectedTopicIds, setSelectedTopicIds] = useState(() => new Set());

  const initMode = searchParams.get('mode') || 'weakest';
  const initTopicId = searchParams.get('topic_id');
  const [module, setModule] = useState('both');
  const [mode, setMode] = useState(initMode);
  const [sessionType, setSessionType] = useState('practice');
  const [sessionConfig, setSessionConfig] = useState(null);

  useEffect(() => {
    if (initTopicId) {
      setSelectedTopicIds(new Set([parseInt(initTopicId, 10)]));
    }
  }, [initTopicId]);

  useEffect(() => {
    if (initMode === 'topic' && initTopicId) {
      setSessionConfig({
        module: 'both',
        mode: 'topic',
        topicIds: [parseInt(initTopicId, 10)],
        count: 10,
        sessionType: 'practice',
      });
    }
    // eslint-disable-next-line
  }, []);

  const loadStats = useCallback(() => {
    api('/api/questions/stats')
      .then((data) => setStats(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (mode !== 'topic') return;
    setTopicCountsLoading(true);
    api('/api/questions/topic-counts')
      .then((d) => {
        setTopicCounts(d.topics || []);
        setTopicCountsLoading(false);
      })
      .catch(() => setTopicCountsLoading(false));
  }, [mode]);

  const toggleTopic = (id) => {
    setSelectedTopicIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const canStart = mode !== 'topic' || selectedTopicIds.size > 0;

  const handleStart = () => {
    setSessionConfig({
      module,
      mode,
      topicIds: Array.from(selectedTopicIds),
      count: 10,
      sessionType,
    });
  };

  const handleSessionExit = (opts = {}) => {
    setSessionConfig(null);
    loadStats();
    if (opts.reviewIncorrect) {
      setTimeout(
        () =>
          setSessionConfig({
            module,
            mode: 'incorrect',
            topicIds: [],
            count: 10,
            sessionType,
          }),
        50
      );
    }
  };

  if (sessionConfig) {
    return (
      <div className="max-w-[1320px] mx-auto px-sp-10 py-sp-4">
        <QuestionSession config={sessionConfig} onExit={handleSessionExit} />
      </div>
    );
  }

  // Topic picker screen when mode === 'topic'
  if (mode === 'topic') {
    const count = selectedTopicIds.size;
    return (
      <div className="max-w-[1320px] mx-auto px-sp-10 py-sp-6">
        <div className="flex items-center justify-between mb-sp-5">
          <h1 className="text-2xl font-bold text-text-primary">MCQ practice</h1>
          <PageToggle options={TOGGLE_OPTS} active="mcq" />
        </div>

        <QuestionStats stats={stats} />

        <div className="bg-bg-card rounded-r-xl border border-border-soft">
          <div className="px-sp-6 py-sp-5">
            <p className="text-[15px] font-semibold text-text-primary mb-sp-4">Choose topics</p>

            {topicCountsLoading ? (
              <div className="py-sp-8 text-center text-sm text-text-muted">Loading topics</div>
            ) : (
              <TopicPicker
                topics={topicCounts}
                selectedIds={selectedTopicIds}
                onToggle={toggleTopic}
              />
            )}

            <div className="flex items-center justify-between">
              <button
                onClick={() => {
                  setMode('weakest');
                  setSelectedTopicIds(new Set());
                }}
                className="px-sp-3 py-sp-2 text-sm font-medium text-text-secondary border border-border-soft rounded-r-md hover:bg-bg-page transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleStart}
                disabled={!canStart}
                className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98]"
              >
                {count > 0
                  ? `Start session (${count} topic${count === 1 ? '' : 's'})`
                  : 'Start session'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1320px] mx-auto px-sp-10 py-sp-6">
      <div className="flex items-center justify-between mb-sp-5">
        <h1 className="text-2xl font-bold text-text-primary">MCQ practice</h1>
        <PageToggle options={TOGGLE_OPTS} active="mcq" />
      </div>

      <QuestionStats stats={stats} />

      <div className="bg-bg-card rounded-r-xl border border-border-soft">
        <div className="px-sp-6 py-sp-5">
          <p className="text-[15px] font-semibold text-text-primary mb-sp-4">Start a session</p>

          <p className="text-sm font-medium text-text-secondary mb-sp-2">Module</p>
          <div className="inline-flex border border-border-soft rounded-r-md overflow-hidden mb-sp-5">
            {MODULES.map((m) => (
              <button
                key={m.value}
                onClick={() => setModule(m.value)}
                className={`px-sp-5 py-sp-2 text-sm font-medium transition-colors ${
                  module === m.value
                    ? 'bg-accent text-white'
                    : 'bg-transparent text-text-secondary hover:bg-bg-page'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          <p className="text-sm font-medium text-text-secondary mb-sp-2">What to practise</p>
          <div className="grid grid-cols-2 gap-sp-2 mb-sp-5">
            {MODES.map((m) => {
              const active = mode === m.value;
              return (
                <div
                  key={m.value}
                  onClick={() => {
                    setMode(m.value);
                    if (m.value !== 'topic') setSelectedTopicIds(new Set());
                  }}
                  className={`rounded-r-lg px-sp-4 py-sp-3 cursor-pointer transition-colors ${
                    active
                      ? 'border-2 border-accent bg-blue-lightest'
                      : 'border border-border-soft hover:border-border-medium'
                  }`}
                >
                  <h3
                    className={`text-sm font-semibold mb-sp-1 ${
                      active ? 'text-accent' : 'text-text-primary'
                    }`}
                  >
                    {m.label}
                  </h3>
                  <p className="text-xs text-text-muted">{m.desc}</p>
                </div>
              );
            })}
          </div>

          <p className="text-sm font-medium text-text-secondary mb-sp-2">Session type</p>
          <div className="inline-flex border border-border-soft rounded-r-md overflow-hidden mb-sp-2">
            <button
              onClick={() => setSessionType('practice')}
              className={`px-sp-5 py-sp-2 text-sm font-medium transition-colors ${
                sessionType === 'practice'
                  ? 'bg-accent text-white'
                  : 'bg-transparent text-text-secondary hover:bg-bg-page'
              }`}
            >
              Practice
            </button>
            <button
              onClick={() => setSessionType('exam')}
              className={`px-sp-5 py-sp-2 text-sm font-medium transition-colors ${
                sessionType === 'exam'
                  ? 'bg-accent text-white'
                  : 'bg-transparent text-text-secondary hover:bg-bg-page'
              }`}
            >
              Exam conditions
            </button>
          </div>
          <p className="text-xs text-text-muted mb-sp-5">
            {sessionType === 'practice'
              ? 'Immediate feedback after each question'
              : 'No feedback until the end. Timed.'}
          </p>

          <button
            onClick={handleStart}
            disabled={!canStart}
            className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98]"
          >
            Start session
          </button>
        </div>
      </div>
    </div>
  );
}
